import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {DEFAULT_UPSTREAM_VEG_ALERT_POLICY} from 'app/components/Alerts/AlertsPoliciesPanel';
import Gravatar from 'app/components/Gravatar';
import {ListItem} from 'app/components/List';
import {AlertEnrollmentKeys, AlertPolicy, ApiFeature} from 'app/modules/Remote/Feature';
import {
  ApiAlertCountsByFeatureId,
  ApiFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {
  ApiOrganization,
  ApiOrganizationUser,
  getAreaUnit,
  getOrgPropertyOverviewColumnSettings,
} from 'app/modules/Remote/Organization';
import * as CONSTANTS from 'app/utils/constants';
import {
  featureCollectionInRegridCoverage,
  featureInRegridCoverage,
} from 'app/utils/featureCollectionUtils';
import {
  APP_PROPERTY_NAMES,
  CollectionConsistency,
  enrollmentStatusForCustomAlert,
} from 'app/utils/featureUtils';
import * as userUtils from 'app/utils/userUtils';

import cs from './PropertyOverview.module.scss';

/**
 * Generates an array of ListItem objects of the provided users to use in a List
 * component.
 *
 * Used both in the filter and assignment setting UIs.
 */
// `showSelectedCheckmark` is opt-in because the filter menus that share this helper pick one
// user at a time, where the highlight alone reads correctly.
export function makeUserListItems(
  organizationUsers: ApiOrganizationUser[],
  isSelectedFn: (user: ApiOrganizationUser) => boolean = () => false,
  showSelectedCheckmark = false
): ListItem<{id: string}>[] {
  return organizationUsers.map((u) => {
    const isSelected = isSelectedFn(u!);
    return {
      id: u!.email,
      text: userUtils.getSuffixedUserName(u!),
      icon: <Gravatar email={u!.email} size={20} />,
      isSelected,
      rightEl: showSelectedCheckmark && isSelected ? <B.Icon icon="tick" /> : undefined,
    };
  });
}

// Configuration object mapping collection consistency to iconography in menus
const iconsByCollectionConsistency: {[key in CollectionConsistency]: B.IconName} = {
  all: 'tick',
  some: 'minus',
  none: 'blank',
};

// How an AlertPolicy is named in the Lookouts menus, and the stand-in used for the veg drop row
// when an org has no policy to name. Both filtering and bulk enrollment match on these ids.
export const menuIdForAlertPolicy = (alertPolicy: AlertPolicy) => String(alertPolicy.id);
export const VEG_DROP_PLACEHOLDER_MENU_ID = 'dummyDefaultVegdropPolicyId';

// Generate ListItems for bulk alert enrollment and filtering
// Omits the parcel alerts when nothing in scope is inside Regrid coverage.
export function getAlertListItems(
  vegetationDropEnrollmentStatus: CollectionConsistency,
  ownershipChangeEnrollmentStatus: CollectionConsistency,
  parcelAreaChangeEnrollmentStatus: CollectionConsistency,
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>,
  alertPolicies: AlertPolicy[],
  selectedFeatures: I.List<I.MapAsRecord<I.ImmutableFields<ApiFeature>>>,
  parcelAlertsInPlan: boolean
): ListItem<{id: string; icon?: React.ReactElement; text: string}>[] {
  const menuItems: ListItem<{
    id: string;
    icon?: React.ReactElement;
    text: string;
  }>[] = [];

  // bring the default vegetation drop alergetAlertListItemst policy to the top of the list
  const defaultVegetationDropAlertPolicy = alertPolicies.find(
    (alertPolicy) => alertPolicy.name === CONSTANTS.ALERT_VEGETATION_DROP_DEFAULT_POLICY_NAME
  );
  menuItems.push({
    id: defaultVegetationDropAlertPolicy
      ? menuIdForAlertPolicy(defaultVegetationDropAlertPolicy)
      : VEG_DROP_PLACEHOLDER_MENU_ID,
    icon: (
      <span className={cs.alertAccentCustomAlert}>
        <B.Icon icon={iconsByCollectionConsistency[vegetationDropEnrollmentStatus]} />
      </span>
    ),
    text: DEFAULT_UPSTREAM_VEG_ALERT_POLICY.name,
  });

  //then add the other Upstream alerts
  menuItems.push(
    ...[
      {
        id: CONSTANTS.ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY,
        icon: (
          <span className={cs.alertAccentCustomAlert}>
            <B.Icon icon={iconsByCollectionConsistency[ownershipChangeEnrollmentStatus]} />
          </span>
        ),
        text: 'Parcel owner change',
      },
      {
        id: CONSTANTS.ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY,
        icon: (
          <span className={cs.alertAccentCustomAlert}>
            <B.Icon icon={iconsByCollectionConsistency[parcelAreaChangeEnrollmentStatus]} />
          </span>
        ),
        text: 'Parcel area change',
      },
    ]
  );

  // finally, any custom alerts (excluding the default veg drop policy)
  alertPolicies.forEach((alertPolicy) => {
    if (alertPolicy.name === CONSTANTS.ALERT_VEGETATION_DROP_DEFAULT_POLICY_NAME) {
      return;
    }
    menuItems.push({
      id: menuIdForAlertPolicy(alertPolicy),
      text: alertPolicy.name,
      icon: (
        <span className={cs.alertAccentCustomAlert}>
          <B.Icon
            icon={
              iconsByCollectionConsistency[
                enrollmentStatusForCustomAlert(selectedFeatures, alertPolicy)
              ]
            }
          />
        </span>
      ),
    });
  });

  // With properties selected this drives bulk enrollment, so one eligible property is enough
  // to be worth offering — the API drops the enrollment for any that aren't. Callers building
  // a filter menu pass no properties, leaving the portfolio as the only thing to go on.
  //
  // The portfolio's bounds enclose every property in it, so when they miss coverage entirely
  // no individual property can be in it. Checking that first keeps a large non-US selection
  // off the per-property loop, which has no early exit precisely when nothing matches.
  const anyInRegridCoverage =
    featureCollectionInRegridCoverage(selectedFeatureCollection) &&
    (selectedFeatures.size
      ? selectedFeatures.some((f) => !!f && featureInRegridCoverage(f))
      : true);

  // Remove the menu options corresponding to parcel ownership and area change alerts when
  // no selected property is in regrid's service area or the plan doesn't include them.
  if (!anyInRegridCoverage || !parcelAlertsInPlan) {
    return menuItems.filter(
      (item) =>
        item.id !== CONSTANTS.ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY &&
        item.id !== CONSTANTS.ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY
    );
  }

  return menuItems;
}

// alertCounts buckets the parcel Lookouts under a fixed alert user id, but the menu names them by
// enrollment key, so matching one against the other needs this translation. Typed over the whole
// union so a new Lookout can't be added without naming the key its alerts land under.
const ALERT_USER_ID_BY_ENROLLMENT_KEY: Record<AlertEnrollmentKeys, string | undefined> = {
  [CONSTANTS.LEGACY_ALERT_VEGETATION_DROP_ENROLLMENT_KEY]: CONSTANTS.ALERT_VEGETATION_DROP_USER_ID,
  [CONSTANTS.ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY]: CONSTANTS.ALERT_OWNERSHIP_CHANGE_USER_ID,
  [CONSTANTS.ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY]:
    CONSTANTS.ALERT_PARCEL_BOUNDARY_CHANGE_USER_ID,
};

// Matches one feature's alertCounts against the ids selected in the Lookouts filter menu, which
// getAlertListItems builds.
export function makeAlertCountFilterPredicate(
  selectedMenuIds: I.Set<string>,
  alertPolicies: AlertPolicy[]
): (alertCounts: ApiAlertCountsByFeatureId[string]) => boolean {
  // Every veg drop policy's alerts are counted under the one veg drop user id, so all of their
  // menu ids match it. With no policy to name, the menu's veg drop row carries the placeholder id.
  const vegetationDropPolicyIds = alertPolicies
    .filter((p) => p.alertType === CONSTANTS.CUSTOM_ALERT_TYPE_VEGETATION_DROP)
    .map(menuIdForAlertPolicy);
  const vegetationDropMenuIds = vegetationDropPolicyIds.length
    ? vegetationDropPolicyIds
    : [VEG_DROP_PLACEHOLDER_MENU_ID];

  const countKeyForMenuId = (menuId: string) =>
    ALERT_USER_ID_BY_ENROLLMENT_KEY[menuId as AlertEnrollmentKeys] ??
    (vegetationDropMenuIds.includes(menuId) ? CONSTANTS.ALERT_VEGETATION_DROP_USER_ID : menuId);

  const selectedCountKeys = new Set(selectedMenuIds.toArray().map(countKeyForMenuId));

  return (alertCounts) => Object.keys(alertCounts).some((key) => selectedCountKeys.has(key));
}

export const makeCustomColumn = (propertyName: string) => {
  return {
    id: propertyName,
    Header: propertyName.length > 20 ? propertyName.slice(0, 20) + '...' : propertyName,
    Filter: () => null,
    filter: 'additionalFeatureProperty',
    // Clips long values to the column width; the cell's title shows the whole thing.
    className: cs.cellAdditionalFeatureProperty,
    accessor: ({features}) => {
      // be extra cautious about null checks since we have no control over what's in these columns
      const value = features?.first()?.getIn(['properties', propertyName]);
      return value == null ? '' : String(value);
    },
    Cell: ({cell}) => {
      const value: string = cell.value;

      if (!value) {
        return null;
      }

      // if the value is parseable as a URL, render it as a link
      const urlRegex = /((https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- ./?%&=]*)?)/gi;
      if (value.match(urlRegex)) {
        return (
          <a
            href={value.startsWith('http') ? value : `https://${value}`}
            target="_blank"
            rel="noopener noreferrer"
            title={value}
          >
            {value}
          </a>
        );
      }

      return <span title={value}>{value}</span>;
    },
  };
};

type ConfigurableColumnId =
  | 'tags'
  | 'area'
  | 'imagery'
  | 'userNoteCount'
  | 'alertNoteCount'
  | 'reportCount'
  | 'assignee'
  | 'reportingDueDate';
/**
 * Generate the column metadata for property overview table columns
 * to list in the configuration menu. We're duplicating the header names and ids
 * from usePropertyTable so that we can use them even outside the context of the
 * table.*/
export function getPropertyOverviewDefaultColumnMetadata(
  organization: I.ImmutableOf<ApiOrganization>
): Array<{id: ConfigurableColumnId | 'name'; Header: string}> {
  const areaUnit = getAreaUnit(organization);
  const areaHeader = areaUnit === 'areaHectare' ? 'Hectares' : 'Acres';

  return [
    {
      id: 'name',
      Header: 'Name',
    },
    {
      id: 'tags',
      Header: 'Tags',
    },
    {
      id: 'area',
      Header: areaHeader,
    },
    {
      id: 'imagery',
      Header: 'High-Res Imagery',
    },
    {
      id: 'userNoteCount',
      Header: 'Notes',
    },
    {
      id: 'alertNoteCount',
      Header: 'Lookouts',
    },
    {
      id: 'reportCount',
      Header: 'Reports',
    },
    {
      id: 'assignee',
      Header: APP_PROPERTY_NAMES[CONSTANTS.APP_PROPERTY_ASSIGNEE_EMAIL],
    },
    {
      id: 'reportingDueDate',
      Header: APP_PROPERTY_NAMES[CONSTANTS.APP_PROPERTY_REPORTING_DUE_DATE],
    },
  ];
}

/**
 * Returns the effective column settings for the property overview table: the
 * org's saved settings when present, otherwise the default column order.
 * Use this whenever reading column settings so that "no saved config" is
 * treated as "use defaults" rather than an empty list.
 */
export function getDefaultPropertyOverviewColumnSettings(
  organization: I.ImmutableOf<ApiOrganization>
): {orderedVisibleColumns: string[]} {
  const raw = getOrgPropertyOverviewColumnSettings(organization);
  if (raw.orderedVisibleColumns.length > 0) {
    return raw;
  }
  const defaultMetadata = getPropertyOverviewDefaultColumnMetadata(organization);
  return {
    orderedVisibleColumns: defaultMetadata.map((col) => col.id),
  };
}
